import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, FileText, Send, Download, Inbox, Wand2, Loader2, Share2, BarChart3, ChevronUp, ChevronDown } from 'lucide-react';
import { AssetsAPI, SubmissionsAPI } from '../api';
import { FormFields, ErrorSummary, validateFields, focusField } from './FormFields';

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File upload' },
  { value: 'signature', label: 'Signature' },
];

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `fk-${Math.random().toString(36).slice(2)}`);
const emptyField = () => ({ _k: uid(), label: 'New field', type: 'text', required: false });
const withKeys = (list) => (list || []).map((f) => ({ ...f, _k: f._k || uid() }));
const stripKeys = (list) => (list || []).map(({ _k, ...rest }) => rest);

const BuildTab = ({ fields, setFields, saving, onSave }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const updateField = (i, key, val) => {
    setFields((f) => f.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  };
  const addField = () => setFields((f) => [...f, emptyField()]);
  const removeField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));
  const move = (i, dir) => setFields((f) => {
    const j = i + dir;
    if (j < 0 || j >= f.length) return f;
    const copy = [...f];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const onDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(i)); } catch (err) { console.debug('[builder] dataTransfer unavailable', err); }
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIndex !== i) setOverIndex(i);
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === i) { setDragIndex(null); setOverIndex(null); return; }
    setFields((f) => {
      const copy = [...f];
      const [moved] = copy.splice(dragIndex, 1);
      copy.splice(i, 0, moved);
      return copy;
    });
    setDragIndex(null);
    setOverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  return (
    <div className="p-4 md:p-6 space-y-3">
      {fields.length === 0 && (
        <div className="text-center py-10 text-[13px] text-[#6F76A7]">
          No fields yet. Click &quot;Add field&quot; to start building.
        </div>
      )}
      {fields.map((f, i) => (
        <div
          key={f._k}
          draggable
          onDragStart={onDragStart(i)}
          onDragOver={onDragOver(i)}
          onDrop={onDrop(i)}
          onDragEnd={onDragEnd}
          className={`border rounded-lg p-3 bg-white flex gap-2 items-start transition-all ${
            dragIndex === i ? 'opacity-40 border-[#0F766E]' : overIndex === i && dragIndex !== null ? 'border-[#0F766E] shadow-md' : 'border-[#E3E5F5] hover:border-[#C8CCE8]'
          }`}
          data-testid={`builder-field-${i}`}
        >
          <div className="flex flex-col items-center pt-1 gap-0.5 shrink-0 text-[#B0B4D0]">
            <GripVertical className="hidden md:block w-4 h-4 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
            <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`builder-field-up-${i}`} className="md:hidden p-1 rounded hover:bg-[#F0F1FB] disabled:opacity-30" aria-label="Move field up"><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} data-testid={`builder-field-down-${i}`} className="md:hidden p-1 rounded hover:bg-[#F0F1FB] disabled:opacity-30" aria-label="Move field down"><ChevronDown className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 grid grid-cols-12 gap-2 min-w-0">
            <input
              value={f.label}
              onChange={(e) => updateField(i, 'label', e.target.value)}
              placeholder="Field label"
              data-testid={`builder-field-label-${i}`}
              className="col-span-12 md:col-span-6 px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551] focus:ring-2 focus:ring-[#0A1551]/10"
            />
            <select
              value={f.type}
              onChange={(e) => updateField(i, 'type', e.target.value)}
              data-testid={`builder-field-type-${i}`}
              className="col-span-7 md:col-span-4 px-2 py-2 border border-[#D5D7ED] rounded-md text-[13px] bg-white focus:outline-none focus:border-[#0A1551]"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <label className="col-span-5 md:col-span-2 flex items-center justify-center gap-1.5 text-[13px] text-[#0A1551] px-2 py-2 border border-[#D5D7ED] rounded-md cursor-pointer bg-white">
              <input
                type="checkbox"
                checked={!!f.required}
                onChange={(e) => updateField(i, 'required', e.target.checked)}
                data-testid={`builder-field-required-${i}`}
                className="accent-[#0F766E]"
              />
              Required
            </label>
            {(f.type === 'dropdown' || f.type === 'checkbox') && (
              <input
                value={(f.options || []).join(', ')}
                onChange={(e) => updateField(i, 'options', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder="Comma-separated options"
                className="col-span-12 px-3 py-2 border border-[#D5D7ED] rounded-md text-[13px] focus:outline-none focus:border-[#0A1551]"
              />
            )}
          </div>
          <button onClick={() => removeField(i)} className="p-2 rounded-md hover:bg-[#FEF2F2] text-[#E5387B] shrink-0" aria-label="Delete field">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex justify-between items-center pt-2">
        <button onClick={addField} className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-dashed border-[#D5D7ED] text-[13px] font-medium text-[#0A1551] hover:bg-white hover:border-[#0F766E] hover:text-[#0F766E] transition-colors">
          <Plus className="w-4 h-4" /> Add field
        </button>
        <button onClick={onSave} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {saving ? 'Saving...' : 'Save fields'}
        </button>
      </div>
    </div>
  );
};

const PreviewTab = ({ asset, fields, onSubmit, submitting }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const update = (label, val) => {
    const next = { ...values, [label]: val };
    setValues(next);
    if (attempted) setErrors(validateFields(fields, next));
  };
  const handleSubmit = async () => {
    setAttempted(true);
    const errs = validateFields(fields, values);
    setErrors(errs);
    if (Object.keys(errs).length) { focusField(fields.find((f) => errs[f.label]).label); return; }
    try {
      await onSubmit(values);
      setValues({}); setErrors({}); setAttempted(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) { setErrors(detail.errors); focusField(Object.keys(detail.errors)[0]); }
    }
  };

  if (!fields || fields.length === 0) {
    return (
      <div className="p-10 text-center text-[13px] text-[#6F76A7]">
        This form has no fields yet. Add some in the Build tab.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-lg mx-auto bg-white border border-[#E3E5F5] rounded-xl p-4 md:p-6 shadow-sm" data-testid="builder-preview">
        <h3 className="text-[18px] font-bold text-[#0A1551] mb-1">{asset.name}</h3>
        {asset.description && <p className="text-[13px] text-[#6F76A7] mb-4">{asset.description}</p>}
        <FormFields fields={fields} values={values} onChange={update} errors={errors} />
        <ErrorSummary errors={errors} fields={fields} onReview={focusField} />
        <button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="builder-preview-submit"
          className="mt-5 w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[14px] font-semibold disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Submit test response
        </button>
      </div>
    </div>
  );
};

const SubmissionsTab = ({ submissions, fields, onExport, onDelete }) => {
  const labels = (fields || []).map((f) => f.label);
  if (!submissions || submissions.length === 0) {
    return (
      <div className="p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#F0F1FB] flex items-center justify-center mb-3">
          <Inbox className="w-7 h-7 text-[#B0B4D0]" />
        </div>
        <p className="text-[14px] font-semibold text-[#0A1551]">No submissions yet</p>
        <p className="text-[12px] text-[#6F76A7] mt-1">Fill out the form in the Preview tab to create one.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] text-[#6F76A7]">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</p>
        <button onClick={onExport} data-testid="builder-export-csv" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551] hover:bg-white">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <div className="border border-[#E3E5F5] rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F9FAFD] border-b border-[#E3E5F5]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-[#0A1551] whitespace-nowrap">Submitted at</th>
              {labels.map((l) => (
                <th key={l} className="text-left px-3 py-2 font-semibold text-[#0A1551] whitespace-nowrap">{l}</th>
              ))}
              <th className="px-3 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => (
              <tr key={s.id} className="border-b border-[#F0F1FB] hover:bg-[#F9FAFD]">
                <td className="px-3 py-2 text-[#6F76A7] whitespace-nowrap">{new Date(s.submitted_at).toLocaleString()}</td>
                {labels.map((l) => (
                  <td key={l} className="px-3 py-2 text-[#0A1551] max-w-[220px] truncate">
                    {Array.isArray(s.data[l]) ? s.data[l].join(', ') : (s.data[l] && typeof s.data[l] === 'object') ? <span className="inline-flex items-center gap-1 text-[#0F766E] font-medium">✍ Signed</span> : (s.data[l] || <span className="text-[#B0B4D0]">—</span>)}
                  </td>
                ))}
                <td className="px-2 py-2">
                  <button onClick={() => onDelete(s.id)} className="p-1 rounded hover:bg-[#FEF2F2] text-[#E5387B]" aria-label="Delete submission">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnalyticsTab = ({ submissions, fields }) => {
  if (!submissions || submissions.length === 0) {
    return (
      <div className="p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#F0F1FB] flex items-center justify-center mb-3">
          <BarChart3 className="w-7 h-7 text-[#B0B4D0]" />
        </div>
        <p className="text-[14px] font-semibold text-[#0A1551]">No data to chart yet</p>
        <p className="text-[12px] text-[#6F76A7] mt-1">Analytics appear after your first submission.</p>
      </div>
    );
  }

  // Group by day (last 14 days)
  const byDay = {};
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const key = d.toISOString().slice(0, 10);
    byDay[key] = 0;
    days.push(key);
  }
  submissions.forEach((s) => {
    const key = new Date(s.submitted_at).toISOString().slice(0, 10);
    if (byDay[key] != null) byDay[key] += 1;
  });
  const maxCount = Math.max(1, ...days.map((k) => byDay[k]));

  // Categorical fields (dropdown / checkbox) counts
  const categorical = (fields || []).filter((f) => f.type === 'dropdown' || f.type === 'checkbox');
  const pieColors = ['#0F766E', '#0AA24F', '#7C5CFF', '#2D8DF0', '#E5387B', '#0E9C8E', '#F5B402', '#4460F7'];

  const buildPie = (field) => {
    const counts = {};
    (field.options || []).forEach((o) => { counts[o] = 0; });
    submissions.forEach((s) => {
      const v = s.data[field.label];
      if (Array.isArray(v)) v.forEach((x) => { if (counts[x] != null) counts[x] += 1; });
      else if (v && counts[v] != null) counts[v] += 1;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return { counts, total };
  };

  const donut = (data, total) => {
    if (total === 0) return null;
    const size = 140;
    const r = 56;
    const cx = size / 2;
    const cy = size / 2;
    const c = 2 * Math.PI * r;
    let offset = 0;
    return (
      <svg width={size} height={size} className="shrink-0">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F0F1FB" strokeWidth="18" />
        {Object.entries(data).map(([opt, v], idx) => {
          if (v === 0) return null;
          const frac = v / total;
          const dash = frac * c;
          const gap = c - dash;
          const rotation = (offset / total) * 360 - 90;
          offset += v;
          return (
            <circle
              key={opt}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={pieColors[idx % pieColors.length]}
              strokeWidth="18"
              strokeDasharray={`${dash} ${gap}`}
              transform={`rotate(${rotation} ${cx} ${cy})`}
            />
          );
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="20" fontWeight="700" fill="#0A1551">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill="#6F76A7">responses</text>
      </svg>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Timeline */}
      <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 md:p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-[15px] font-bold text-[#0A1551]">Submissions over time</h3>
          <span className="text-[12px] text-[#6F76A7]">Last 14 days · {submissions.length} total</span>
        </div>
        <div className="flex items-end gap-1.5 h-40">
          {days.map((k) => {
            const v = byDay[k];
            const h = (v / maxCount) * 100;
            return (
              <div key={k} className="flex-1 flex flex-col items-center gap-1.5 group">
                <div className="text-[10px] font-semibold text-[#0A1551] opacity-0 group-hover:opacity-100 transition-opacity">{v}</div>
                <div
                  className="w-full rounded-t bg-gradient-to-t from-[#FFB577] to-[#0F766E] hover:from-[#FF9457] hover:to-[#0B5F58] transition-colors"
                  style={{ height: `${Math.max(3, h)}%` }}
                  title={`${k}: ${v} submission${v !== 1 ? 's' : ''}`}
                ></div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-[#6F76A7] mt-2 px-1">
          <span>{new Date(days[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
          <span>{new Date(days[days.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Categorical pies */}
      {categorical.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categorical.map((field) => {
            const { counts, total } = buildPie(field);
            return (
              <div key={field.label} className="bg-white border border-[#E3E5F5] rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-[#0A1551] mb-3">{field.label}</h3>
                <div className="flex items-center gap-4">
                  {donut(counts, total) || (
                    <div className="w-[140px] h-[140px] rounded-full border-8 border-[#F0F1FB] flex items-center justify-center text-[12px] text-[#6F76A7]">No data</div>
                  )}
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {Object.entries(counts).map(([opt, v], idx) => (
                      <div key={opt} className="flex items-center gap-2 text-[12px]">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: pieColors[idx % pieColors.length] }}></span>
                        <span className="text-[#0A1551] truncate flex-1">{opt}</span>
                        <span className="text-[#6F76A7] font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TabButton = ({ id, label, icon: Icon, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 md:px-4 py-3 text-[13px] font-semibold border-b-2 whitespace-nowrap transition-colors ${
      active ? 'border-[#0F766E] text-[#0F766E]' : 'border-transparent text-[#6F76A7] hover:text-[#0A1551]'
    }`}
    data-testid={`builder-tab-${id}`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count != null && (
      <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#E6F4F2] text-[#0F766E]' : 'bg-[#F0F1FB] text-[#6F76A7]'}`}>
        {count}
      </span>
    )}
  </button>
);

const FormBuilder = ({ open, onClose, asset, onUpdated }) => {
  const [tab, setTab] = useState('build');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (asset) {
      setFields(withKeys(asset.fields));
      setTab('build');
      SubmissionsAPI.list(asset.id).then(setSubmissions).catch((err) => { console.error('[builder] load submissions failed', err); setSubmissions([]); });
    }
  }, [asset]);

  if (!open || !asset) return null;

  const saveFields = async () => {
    setSaving(true);
    try {
      const updated = await AssetsAPI.update(asset.id, { fields: stripKeys(fields) });
      onUpdated && onUpdated(updated);
    } finally {
      setSaving(false);
    }
  };

  const submitForm = async (values) => {
    setSubmitting(true);
    try {
      const sub = await SubmissionsAPI.create(asset.id, values);
      setSubmissions((s) => [sub, ...s]);
      const updated = await AssetsAPI.get(asset.id);
      if (updated) onUpdated && onUpdated(updated);
      setTab('submissions');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSubmission = async (subId) => {
    setSubmissions((s) => s.filter((x) => x.id !== subId));
    try {
      await SubmissionsAPI.remove(asset.id, subId);
      const updated = await AssetsAPI.get(asset.id);
      if (updated) onUpdated && onUpdated(updated);
    } catch (e) {
      console.error('[builder] delete submission failed, reloading', e);
      SubmissionsAPI.list(asset.id).then(setSubmissions).catch((err) => console.error('[builder] reload submissions failed', err));
    }
  };

  const exportCSV = () => {
    const labels = (fields || []).map((f) => f.label);
    const rows = [['Submitted at', ...labels]];
    submissions.forEach((s) => {
      rows.push([
        new Date(s.submitted_at).toISOString(),
        ...labels.map((l) => {
          const v = s.data[l];
          const str = Array.isArray(v) ? v.join('; ') : (v && typeof v === 'object') ? `Signed (${v.points || 0} points)` : (v || '');
          return `"${String(str).replace(/"/g, '""')}"`;
        }),
      ]);
    });
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${asset.name.replace(/[^a-z0-9]+/gi, '_')}_submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/f/${asset.share_id}`;
    try { navigator.clipboard.writeText(url); } catch (err) { console.error('[builder] clipboard write failed', err); }
    // eslint-disable-next-line no-alert
    window.alert(`Share link copied:\n${url}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-[#0A1551]/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-[900px] h-full sm:h-[85vh] bg-[#F5F6FB] rounded-none sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-[#E3E5F5] flex items-center gap-2 md:gap-3 bg-white">
          <div className="hidden sm:flex w-10 h-10 rounded-lg bg-[#E6F4F2] items-center justify-center">
            <FileText className="w-5 h-5 text-[#0F766E]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[17px] font-bold text-[#0A1551] truncate">{asset.name}</h2>
            <p className="text-[12px] text-[#6F76A7]">{asset.type} · {submissions.length} total submissions</p>
          </div>
          {asset.share_id && (
            <button
              onClick={copyShareLink}
              data-testid="builder-share-button"
              className="flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551] hover:bg-[#F5F6FB] transition-colors"
              title="Copy public share link"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Share</span>
            </button>
          )}
          <button onClick={onClose} data-testid="builder-close" className="p-2 rounded-lg hover:bg-[#F5F6FB]">
            <X className="w-4 h-4 text-[#6F76A7]" />
          </button>
        </div>
        <div className="bg-white border-b border-[#E3E5F5] px-2 md:px-4 flex items-center gap-1 md:gap-2 overflow-x-auto">
          <TabButton id="build" label="Build" icon={Wand2} count={fields.length} active={tab === 'build'} onClick={() => setTab('build')} />
          <TabButton id="preview" label="Preview" icon={FileText} active={tab === 'preview'} onClick={() => setTab('preview')} />
          <TabButton id="submissions" label="Submissions" icon={Inbox} count={submissions.length} active={tab === 'submissions'} onClick={() => setTab('submissions')} />
          <TabButton id="analytics" label="Analytics" icon={BarChart3} active={tab === 'analytics'} onClick={() => setTab('analytics')} />
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === 'build' && <BuildTab fields={fields} setFields={setFields} saving={saving} onSave={saveFields} />}
          {tab === 'preview' && <PreviewTab asset={asset} fields={fields} onSubmit={submitForm} submitting={submitting} />}
          {tab === 'submissions' && <SubmissionsTab submissions={submissions} fields={fields} onExport={exportCSV} onDelete={deleteSubmission} />}
          {tab === 'analytics' && <AnalyticsTab submissions={submissions} fields={fields} />}
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
